// =============================================================================
// HISTORIQUE MEMBRE - PDF des affectations passées d'un membre
// Design aligné sur la charte B8ild (bleu roi + doré)
// =============================================================================

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sharedStyles, COLORS } from './pdfStyles';
import { formatMontant, formatDate } from './pdfUtils';
import type { AffectationHistorique, AffectationsParAnnee } from '@/hooks/useMemberHistory';

// =============================================================================
// STYLES SPÉCIFIQUES
// =============================================================================

const styles = StyleSheet.create({
  // Encart membre (bleu roi comme encart chantier)
  membreBox: {
    backgroundColor: COLORS.bleuRoi,
    borderRadius: 8,
    padding: 20,
    marginBottom: 20,
  },
  membreNom: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginBottom: 8,
  },
  membrePoste: {
    fontSize: 12,
    color: COLORS.dore,
    marginBottom: 12,
  },
  membreInfoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  membreLabel: {
    fontSize: 10,
    color: COLORS.dore,
    width: 100,
  },
  membreValue: {
    fontSize: 10,
    color: COLORS.blanc,
    flex: 1,
  },

  // Année groupée
  anneeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: COLORS.grisClair,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 8,
    marginTop: 12,
  },
  anneeText: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  anneeBadge: {
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  anneeBadgeText: {
    fontSize: 8,
    color: COLORS.blanc,
    fontFamily: 'Helvetica-Bold',
  },
  anneeStats: {
    flexDirection: 'row',
    gap: 15,
  },
  anneeStat: {
    fontSize: 9,
    color: COLORS.grisTexte,
  },
  anneeStatValue: {
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },

  // Affectation
  affectationCard: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    padding: 10,
    marginBottom: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  affectationLeft: {
    flex: 1,
  },
  affectationRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  chantierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  chantierDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  chantierNom: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
  },
  badge: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
    marginLeft: 6,
  },
  badgeRetard: {
    backgroundColor: '#FEF3C7',
  },
  badgeRetardText: {
    fontSize: 7,
    color: '#D97706',
    fontFamily: 'Helvetica-Bold',
  },
  badgeReprise: {
    backgroundColor: '#D1FAE5',
  },
  badgeRepriseText: {
    fontSize: 7,
    color: '#059669',
    fontFamily: 'Helvetica-Bold',
  },
  affectationDetails: {
    flexDirection: 'row',
    gap: 8,
  },
  detailText: {
    fontSize: 9,
    color: COLORS.grisTexte,
  },
  heuresSupRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  heuresSupText: {
    fontSize: 9,
    color: '#D97706',
    fontFamily: 'Helvetica-Bold',
  },
  coutPrincipal: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  coutHS: {
    fontSize: 8,
    color: COLORS.grisTexte,
    marginTop: 2,
  },
});

// =============================================================================
// PROPS
// =============================================================================

interface MembrePourPDF {
  prenom: string;
  nom: string;
  poste: string;
  specialite: string;
  taux_horaire: number;
  coefficient_charges: number;
  heures_par_jour: number;
}

interface HistoriqueMemberPDFProps {
  membre: MembrePourPDF;
  affectationsParAnnee: AffectationsParAnnee[];
  totalJoursOuvres: number;
  totalCoutMO: number;
  nombreChantiers: number;
  entrepriseNom?: string;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function HistoriqueMemberPDF({
  membre,
  affectationsParAnnee,
  totalJoursOuvres,
  totalCoutMO,
  nombreChantiers,
  entrepriseNom = 'Mon entreprise',
}: HistoriqueMemberPDFProps) {
  const dateGeneration = format(new Date(), "d MMMM yyyy", { locale: fr });
  const coutJournalier = membre.taux_horaire * (membre.coefficient_charges || 1.85) * (membre.heures_par_jour || 8);

  return (
    <Document>
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>HISTORIQUE MEMBRE</Text>
            <Text style={sharedStyles.headerSubtitle}>{dateGeneration}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* Encart membre */}
          <View style={styles.membreBox}>
            <Text style={styles.membreNom}>{membre.prenom} {membre.nom}</Text>
            <Text style={styles.membrePoste}>{membre.poste}</Text>
            <View style={styles.membreInfoRow}>
              <Text style={styles.membreLabel}>Specialite</Text>
              <Text style={styles.membreValue}>{membre.specialite || 'Non renseignee'}</Text>
            </View>
            <View style={styles.membreInfoRow}>
              <Text style={styles.membreLabel}>Taux horaire</Text>
              <Text style={styles.membreValue}>{membre.taux_horaire.toFixed(2)} EUR/h</Text>
            </View>
            <View style={styles.membreInfoRow}>
              <Text style={styles.membreLabel}>Cout journalier</Text>
              <Text style={styles.membreValue}>{coutJournalier.toFixed(2)} EUR (7h, charges incluses)</Text>
            </View>
          </View>

          {/* KPIs totaux */}
          <View style={sharedStyles.kpiRow}>
            <View style={sharedStyles.kpiCard}>
              <Text style={sharedStyles.kpiTitle}>JOURS TRAVAILLES</Text>
              <Text style={sharedStyles.kpiValue}>{totalJoursOuvres.toFixed(1)}</Text>
            </View>
            <View style={[sharedStyles.kpiCard, sharedStyles.kpiCardHighlight]}>
              <Text style={sharedStyles.kpiTitle}>COUT MO TOTAL</Text>
              <Text style={[sharedStyles.kpiValue, { color: COLORS.bleuRoi }]}>
                {formatMontant(totalCoutMO)}
              </Text>
            </View>
            <View style={sharedStyles.kpiCard}>
              <Text style={sharedStyles.kpiTitle}>CHANTIERS</Text>
              <Text style={sharedStyles.kpiValue}>{nombreChantiers}</Text>
            </View>
          </View>

          <View style={sharedStyles.separator} />

          {/* Liste par année */}
          {affectationsParAnnee.map((groupe) => (
            <View key={groupe.annee} wrap={false}>
              {/* Header année */}
              <View style={styles.anneeHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <Text style={styles.anneeText}>{groupe.annee}</Text>
                  <View style={styles.anneeBadge}>
                    <Text style={styles.anneeBadgeText}>
                      {groupe.affectations.length} affectation{groupe.affectations.length > 1 ? 's' : ''}
                    </Text>
                  </View>
                </View>
                <View style={styles.anneeStats}>
                  <Text style={styles.anneeStat}>
                    <Text style={styles.anneeStatValue}>{groupe.totalJours.toFixed(1)}</Text> jours
                  </Text>
                  <Text style={styles.anneeStat}>
                    <Text style={styles.anneeStatValue}>{formatMontant(groupe.totalCout)}</Text>
                  </Text>
                </View>
              </View>

              {/* Affectations de l'année */}
              {groupe.affectations.map((aff) => (
                <AffectationRow key={aff.id} affectation={aff} />
              ))}
            </View>
          ))}

          {/* Message si aucune affectation */}
          {affectationsParAnnee.length === 0 && (
            <View style={{ alignItems: 'center', paddingVertical: 40 }}>
              <Text style={{ fontSize: 12, color: COLORS.grisTexte }}>
                Aucune affectation enregistree pour ce membre.
              </Text>
            </View>
          )}
        </View>

        {/* Footer - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              {entrepriseNom} - Genere avec B8ild
            </Text>
            <Text style={sharedStyles.footerPage}>
              {dateGeneration}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

// =============================================================================
// COMPOSANT AFFECTATION
// =============================================================================

function AffectationRow({ affectation }: { affectation: AffectationHistorique }) {
  const isRetard = affectation.notes?.includes('Retard') ?? false;
  const isReprise = affectation.notes?.includes('Reprise') ?? false;
  const dateDebut = formatDate(affectation.date_debut);
  const dateFin = formatDate(affectation.date_fin);
  const isSameDay = affectation.date_debut === affectation.date_fin;

  return (
    <View style={styles.affectationCard}>
      <View style={styles.affectationLeft}>
        {/* Nom du chantier */}
        <View style={styles.chantierRow}>
          <View
            style={[
              styles.chantierDot,
              { backgroundColor: affectation.chantier.couleur || '#6B7280' },
            ]}
          />
          <Text style={styles.chantierNom}>{affectation.chantier.nom_chantier}</Text>
          {isRetard && (
            <View style={[styles.badge, styles.badgeRetard]}>
              <Text style={styles.badgeRetardText}>RETARD</Text>
            </View>
          )}
          {isReprise && (
            <View style={[styles.badge, styles.badgeReprise]}>
              <Text style={styles.badgeRepriseText}>REPRISE</Text>
            </View>
          )}
        </View>

        {/* Détails */}
        <View style={styles.affectationDetails}>
          <Text style={styles.detailText}>
            {isSameDay ? dateDebut : `${dateDebut} - ${dateFin}`}
          </Text>
          <Text style={styles.detailText}>|</Text>
          <Text style={styles.detailText}>{affectation.jours_travailles} j</Text>
          {affectation.heures_par_jour && (
            <>
              <Text style={styles.detailText}>|</Text>
              <Text style={styles.detailText}>{affectation.heures_par_jour}h/j</Text>
            </>
          )}
          {affectation.total_heures && affectation.total_heures > 0 && (
            <>
              <Text style={styles.detailText}>|</Text>
              <Text style={[styles.detailText, { fontFamily: 'Helvetica-Bold' }]}>
                {affectation.total_heures}h total
              </Text>
            </>
          )}
        </View>

        {/* Heures sup */}
        {affectation.heures_sup && affectation.heures_sup.total_heures > 0 && (
          <View style={styles.heuresSupRow}>
            <Text style={styles.heuresSupText}>
              +{affectation.heures_sup.total_heures}h sup ({formatMontant(affectation.heures_sup.total_cout)})
            </Text>
          </View>
        )}
      </View>

      {/* Coût */}
      <View style={styles.affectationRight}>
        <Text style={styles.coutPrincipal}>{formatMontant(affectation.cout_mo ?? 0)}</Text>
        {affectation.heures_sup && affectation.heures_sup.total_cout > 0 && (
          <Text style={styles.coutHS}>+ {formatMontant(affectation.heures_sup.total_cout)} HS</Text>
        )}
      </View>
    </View>
  );
}
